import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import InputAdornment from '@mui/material/InputAdornment';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import SearchIcon from '@mui/icons-material/Search';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import AddIcon from '@mui/icons-material/Add';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PeopleIcon from '@mui/icons-material/People';
import ErrorSnackbar from '../components/ErrorSnackbar';
import LoadingSpinner from '../components/LoadingSpinner';
import apiService from '../services/ApiService';

const LANGUAGES = ['all', 'es', 'en', 'fr', 'de', 'pt'];

function tomorrow() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().split('T')[0];
}

// ─── Topics Tab ──────────────────────────────────────────────────────────────

function TopicsTab({ t, isMobile }) {
  const [topicText, setTopicText] = useState('');
  const [topicDate, setTopicDate] = useState(tomorrow());
  const [upcoming, setUpcoming] = useState([]);
  const [loadingUpcoming, setLoadingUpcoming] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [topicsStatus, setTopicsStatus] = useState([]);
  const [loadingTopics, setLoadingTopics] = useState(true);
  const [todayTopic, setTodayTopic] = useState(null);
  const [loadingToday, setLoadingToday] = useState(true);
  const [triggeringId, setTriggeringId] = useState(null);
  const [confirmTriggerTopic, setConfirmTriggerTopic] = useState(null);
  const [playersDialog, setPlayersDialog] = useState({ open: false, topic: '', date: '', count: 0, players: [], loading: false });
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });

  const todayStr = new Date().toISOString().split('T')[0];

  const loadUpcoming = useCallback(async () => {
    setLoadingUpcoming(true);
    try {
      const data = await apiService.get('/api/v1/topics/upcoming');
      setUpcoming(Array.isArray(data) ? data.filter((t) => t.date > todayStr) : []);
    } catch {
      setUpcoming([]);
    } finally {
      setLoadingUpcoming(false);
    }
  }, [todayStr]);

  const loadTopicsStatus = useCallback(async () => {
    setLoadingTopics(true);
    try {
      const data = await apiService.get('/api/v1/admin/topics-status');
      setTopicsStatus(Array.isArray(data) ? data : []);
    } catch {
      setTopicsStatus([]);
    } finally {
      setLoadingTopics(false);
    }
  }, []);

  const loadTodayTopic = useCallback(async () => {
    setLoadingToday(true);
    try {
      const data = await apiService.get('/api/v1/admin/today-topic');
      setTodayTopic(data);
    } catch {
      setTodayTopic(null);
    } finally {
      setLoadingToday(false);
    }
  }, []);

  useEffect(() => {
    loadUpcoming();
    loadTopicsStatus();
    loadTodayTopic();
  }, [loadUpcoming, loadTopicsStatus, loadTodayTopic]);

  const handleCreate = async () => {
    if (!topicText.trim()) return;
    setSubmitting(true);
    try {
      await apiService.post('/api/v1/topics/create', { topic: topicText.trim(), date: topicDate });
      setTopicText('');
      setTopicDate(tomorrow());
      setSnack({ open: true, message: t('admin.topics.createSuccess'), severity: 'success' });
      loadUpcoming();
      loadTodayTopic();
    } catch (err) {
      setSnack({ open: true, message: err.message || t('common.error'), severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleTriggerForTopic = async () => {
    if (!confirmTriggerTopic) return;
    const { id, date } = confirmTriggerTopic;
    setConfirmTriggerTopic(null);
    setTriggeringId(id);
    try {
      await apiService.post(`/api/v1/admin/trigger/topic/${id}`);
      setSnack({ open: true, message: t('admin.config.triggerForDateSuccess', { date }), severity: 'success' });
      loadTopicsStatus();
    } catch (err) {
      setSnack({ open: true, message: err.message || t('common.error'), severity: 'error' });
    } finally {
      setTriggeringId(null);
    }
  };

  const handleViewPlayers = async (row) => {
    setPlayersDialog({ open: true, topic: row.topic, date: row.date, count: 0, players: [], loading: true });
    try {
      const data = await apiService.get(`/api/v1/admin/topics/${row.id}/players`);
      setPlayersDialog({ open: true, topic: data.topic, date: data.date, count: data.count, players: data.players, loading: false });
    } catch {
      setPlayersDialog((s) => ({ ...s, loading: false }));
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

      {/* ── Tema de hoy ── */}
      <Card variant="outlined" sx={{ borderColor: 'primary.main' }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            {t('admin.topics.todayTitle')}
          </Typography>
          {loadingToday ? (
            <LoadingSpinner />
          ) : !todayTopic ? (
            <Typography variant="body2" color="text.secondary">
              {t('admin.topics.noTopicToday')}
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Typography variant="h6">{todayTopic.topic}</Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                <Chip
                  icon={<PeopleIcon />}
                  label={t('admin.topics.statPlayers', { count: todayTopic.playerCount })}
                  color="primary"
                  variant="outlined"
                  size="small"
                />
                <Chip
                  label={t('admin.topics.statWords', { count: todayTopic.wordCount })}
                  variant="outlined"
                  size="small"
                />
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<PeopleIcon />}
                  onClick={() => handleViewPlayers(todayTopic)}
                  sx={{ ml: 'auto' }}
                >
                  {t('admin.topics.playersButton')}
                </Button>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            {t('admin.topics.createTitle')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <TextField
              label={t('admin.topics.topicLabel')}
              value={topicText}
              onChange={(e) => setTopicText(e.target.value)}
              size="small"
              sx={{ flex: 1, minWidth: 180 }}
              inputProps={{ maxLength: 100 }}
            />
            <TextField
              label={t('admin.topics.dateLabel')}
              type="date"
              value={topicDate}
              onChange={(e) => setTopicDate(e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
              sx={{ width: 160 }}
            />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreate}
              disabled={submitting || !topicText.trim()}
            >
              {t('admin.topics.createButton')}
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            {t('admin.topics.upcomingTitle')}
          </Typography>
          {loadingUpcoming ? (
            <LoadingSpinner />
          ) : upcoming.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t('admin.topics.noUpcoming')}
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('admin.topics.colDate')}</TableCell>
                    <TableCell>{t('admin.topics.colTopic')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {upcoming.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>{row.date}</TableCell>
                      <TableCell>{row.topic}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            {t('admin.config.topicsStatusTitle')}
          </Typography>
          {loadingTopics ? (
            <LoadingSpinner />
          ) : topicsStatus.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t('admin.config.topicsStatusEmpty')}
            </Typography>
          ) : isMobile ? (
            <Stack divider={<Divider />}>
              {topicsStatus.map((row) => (
                <Box key={row.id} sx={{ py: 1.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">{row.date}</Typography>
                    <Chip
                      label={row.processed ? t('admin.config.processed') : t('admin.config.notProcessed')}
                      color={row.processed ? 'success' : 'warning'}
                      size="small"
                    />
                  </Box>
                  <Typography variant="body2" sx={{ mb: 0.75 }}>{row.topic}</Typography>
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    <Button
                      size="small"
                      variant="outlined"
                      color="warning"
                      startIcon={<PlayArrowIcon />}
                      disabled={row.processed || triggeringId === row.id}
                      onClick={() => setConfirmTriggerTopic(row)}
                    >
                      {t('admin.config.triggerForDateButton')}
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<PeopleIcon />}
                      onClick={() => handleViewPlayers(row)}
                    >
                      {t('admin.topics.playersButton')}
                    </Button>
                  </Box>
                </Box>
              ))}
            </Stack>
          ) : (
            <TableContainer>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('admin.topics.colDate')}</TableCell>
                    <TableCell>{t('admin.topics.colTopic')}</TableCell>
                    <TableCell>{t('admin.config.colProcessed')}</TableCell>
                    <TableCell>{t('admin.users.colActions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {topicsStatus.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>{row.date}</TableCell>
                      <TableCell>{row.topic}</TableCell>
                      <TableCell>
                        <Chip
                          label={row.processed ? t('admin.config.processed') : t('admin.config.notProcessed')}
                          color={row.processed ? 'success' : 'warning'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            color="warning"
                            startIcon={<PlayArrowIcon />}
                            disabled={row.processed || triggeringId === row.id}
                            onClick={() => setConfirmTriggerTopic(row)}
                          >
                            {t('admin.config.triggerForDateButton')}
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<PeopleIcon />}
                            onClick={() => handleViewPlayers(row)}
                          >
                            {t('admin.topics.playersButton')}
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Dialog open={playersDialog.open} onClose={() => setPlayersDialog((s) => ({ ...s, open: false }))} maxWidth="sm" fullWidth>
        <DialogTitle>
          {t('admin.topics.playersDialogTitle', { topic: playersDialog.topic, date: playersDialog.date })}
        </DialogTitle>
        <DialogContent>
          {playersDialog.loading ? (
            <LoadingSpinner />
          ) : playersDialog.players.length === 0 ? (
            <DialogContentText>{t('admin.topics.noPlayers')}</DialogContentText>
          ) : (
            <>
              <DialogContentText sx={{ mb: 1 }}>
                {t('admin.topics.playersCount', { count: playersDialog.count })}
              </DialogContentText>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('admin.users.colName')}</TableCell>
                      <TableCell>{t('admin.users.colEmail')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {playersDialog.players.map((p) => (
                      <TableRow key={p.id}>
                        <TableCell>{p.name || '—'}</TableCell>
                        <TableCell>{p.email}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPlayersDialog((s) => ({ ...s, open: false }))}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!confirmTriggerTopic} onClose={() => setConfirmTriggerTopic(null)}>
        <DialogTitle>{t('admin.config.triggerForDateConfirmTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('admin.config.triggerForDateConfirmText', {
              date: confirmTriggerTopic?.date || '',
              topic: confirmTriggerTopic?.topic || '',
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmTriggerTopic(null)}>{t('common.cancel')}</Button>
          <Button onClick={handleTriggerForTopic} color="warning" variant="contained">
            {t('common.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      <ErrorSnackbar
        open={snack.open}
        message={snack.message}
        severity={snack.severity}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      />
    </Box>
  );
}

// ─── Users Tab ───────────────────────────────────────────────────────────────

function UsersTab({ t, isMobile }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiService.get('/api/v1/users/list');
      setUsers(data);
    } catch {
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const handlePromote = async (user) => {
    try {
      await apiService.put(`/api/v1/users/${user.id}/promote`);
      setSnack({ open: true, message: t('admin.users.promoteSuccess', { name: user.name || user.email }), severity: 'success' });
      loadUsers();
    } catch (err) {
      setSnack({ open: true, message: err.message || t('common.error'), severity: 'error' });
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await apiService.delete(`/api/v1/users/delete/${confirmDelete.id}`);
      setSnack({ open: true, message: t('admin.users.deleteSuccess', { name: confirmDelete.name || confirmDelete.email }), severity: 'success' });
      loadUsers();
    } catch (err) {
      setSnack({ open: true, message: err.message || t('common.error'), severity: 'error' });
    } finally {
      setConfirmDelete(null);
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <Box>
      <Card>
        <CardContent>
          {isMobile ? (
            <Stack divider={<Divider />}>
              {users.map((user) => (
                <Box key={user.id} sx={{ py: 1.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.25 }}>
                    <Typography variant="body2" fontWeight={500}>{user.name || '—'}</Typography>
                    <Chip
                      label={user.role}
                      color={user.role === 'ADMIN' ? 'warning' : 'default'}
                      size="small"
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {user.lastLogin ? new Date(user.lastLogin).toLocaleString() : t('admin.users.neverLoggedIn')}
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                      {user.email}
                    </Typography>
                    {user.role !== 'ADMIN' && (
                      <Box sx={{ display: 'flex', gap: 0.5, ml: 1, flexShrink: 0 }}>
                        <IconButton
                          size="small"
                          color="warning"
                          title={t('admin.users.promoteButton')}
                          onClick={() => handlePromote(user)}
                        >
                          <AdminPanelSettingsIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          title={t('admin.users.deleteButton')}
                          onClick={() => setConfirmDelete(user)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                </Box>
              ))}
            </Stack>
          ) : (
            <TableContainer>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>{t('admin.users.colName')}</TableCell>
                    <TableCell>{t('admin.users.colEmail')}</TableCell>
                    <TableCell>{t('admin.users.colRole')}</TableCell>
                    <TableCell>{t('admin.users.colLastLogin')}</TableCell>
                    <TableCell>{t('admin.users.colActions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>{user.id}</TableCell>
                      <TableCell>{user.name || '—'}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        <Chip
                          label={user.role}
                          color={user.role === 'ADMIN' ? 'warning' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {user.lastLogin ? new Date(user.lastLogin).toLocaleString() : '—'}
                      </TableCell>
                      <TableCell>
                        {user.role !== 'ADMIN' && (
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <IconButton
                              size="small"
                              color="warning"
                              title={t('admin.users.promoteButton')}
                              onClick={() => handlePromote(user)}
                            >
                              <AdminPanelSettingsIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              title={t('admin.users.deleteButton')}
                              onClick={() => setConfirmDelete(user)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>{t('admin.users.deleteConfirmTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('admin.users.deleteConfirmText', {
              name: confirmDelete?.name || confirmDelete?.email || '',
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>{t('common.cancel')}</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            {t('common.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      <ErrorSnackbar
        open={snack.open}
        message={snack.message}
        severity={snack.severity}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      />
    </Box>
  );
}

// ─── Banned Words Tab ─────────────────────────────────────────────────────────

function BannedWordsTab({ t }) {
  const [words, setWords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newWord, setNewWord] = useState('');
  const [newLang, setNewLang] = useState('all');
  const [submitting, setSubmitting] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });
  const [filterText, setFilterText] = useState('');
  const [sortField, setSortField] = useState('word');
  const [sortDir, setSortDir] = useState('asc');

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const displayedWords = useMemo(() => {
    let result = words;
    if (filterText.trim()) {
      const lower = filterText.trim().toLowerCase();
      result = result.filter((w) => w.word.toLowerCase().includes(lower));
    }
    return [...result].sort((a, b) => {
      let aVal = a[sortField] ?? '';
      let bVal = b[sortField] ?? '';
      if (sortField === 'createdAt') {
        aVal = aVal ? new Date(aVal).getTime() : 0;
        bVal = bVal ? new Date(bVal).getTime() : 0;
      } else {
        aVal = String(aVal).toLowerCase();
        bVal = String(bVal).toLowerCase();
      }
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [words, filterText, sortField, sortDir]);

  const loadWords = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiService.get('/api/v1/banned-words');
      setWords(data);
    } catch {
      setWords([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadWords(); }, [loadWords]);

  const handleAdd = async () => {
    if (!newWord.trim()) return;
    setSubmitting(true);
    try {
      await apiService.post('/api/v1/banned-words', { word: newWord.trim(), language: newLang });
      setNewWord('');
      setNewLang('all');
      setSnack({ open: true, message: t('admin.bannedWords.addSuccess'), severity: 'success' });
      loadWords();
    } catch (err) {
      setSnack({ open: true, message: err.message || t('common.error'), severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggle = async (word) => {
    try {
      await apiService.put(`/api/v1/banned-words/${word.id}/toggle`, { active: !word.active });
      loadWords();
    } catch (err) {
      setSnack({ open: true, message: err.message || t('common.error'), severity: 'error' });
    }
  };

  const handleDelete = async (word) => {
    try {
      await apiService.delete(`/api/v1/banned-words/${word.id}`);
      setSnack({ open: true, message: t('admin.bannedWords.deleteSuccess', { word: word.word }), severity: 'success' });
      loadWords();
    } catch (err) {
      setSnack({ open: true, message: err.message || t('common.error'), severity: 'error' });
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Card>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            {t('admin.bannedWords.addTitle')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <TextField
              label={t('admin.bannedWords.wordLabel')}
              value={newWord}
              onChange={(e) => setNewWord(e.target.value)}
              size="small"
              sx={{ flex: 1, minWidth: 150 }}
              inputProps={{ maxLength: 50 }}
              onKeyDown={(e) => { if (e.key === 'Enter') handleAdd(); }}
            />
            <FormControl size="small" sx={{ minWidth: 100 }}>
              <InputLabel>{t('admin.bannedWords.langLabel')}</InputLabel>
              <Select
                value={newLang}
                label={t('admin.bannedWords.langLabel')}
                onChange={(e) => setNewLang(e.target.value)}
              >
                {LANGUAGES.map((l) => (
                  <MenuItem key={l} value={l}>{l}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAdd}
              disabled={submitting || !newWord.trim()}
            >
              {t('admin.bannedWords.addButton')}
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <TextField
            size="small"
            fullWidth
            placeholder={t('admin.bannedWords.filterPlaceholder')}
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />
          {loading ? (
            <LoadingSpinner />
          ) : (
            <TableContainer>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <TableSortLabel
                        active={sortField === 'word'}
                        direction={sortField === 'word' ? sortDir : 'asc'}
                        onClick={() => handleSort('word')}
                      >
                        {t('admin.bannedWords.colWord')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortField === 'language'}
                        direction={sortField === 'language' ? sortDir : 'asc'}
                        onClick={() => handleSort('language')}
                      >
                        {t('admin.bannedWords.colLang')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortField === 'active'}
                        direction={sortField === 'active' ? sortDir : 'asc'}
                        onClick={() => handleSort('active')}
                      >
                        {t('admin.bannedWords.colStatus')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortField === 'createdAt'}
                        direction={sortField === 'createdAt' ? sortDir : 'asc'}
                        onClick={() => handleSort('createdAt')}
                      >
                        {t('admin.bannedWords.colCreated')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>{t('admin.users.colActions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayedWords.map((w) => (
                    <TableRow key={w.id}>
                      <TableCell>{w.word}</TableCell>
                      <TableCell>{w.language}</TableCell>
                      <TableCell>
                        <Chip
                          label={w.active ? t('admin.active') : t('admin.inactive')}
                          color={w.active ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {w.createdAt ? new Date(w.createdAt).toLocaleDateString() : '—'}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <IconButton
                            size="small"
                            color={w.active ? 'default' : 'success'}
                            title={w.active ? t('admin.bannedWords.deactivate') : t('admin.bannedWords.activate')}
                            onClick={() => handleToggle(w)}
                          >
                            {w.active ? <ToggleOnIcon fontSize="small" /> : <ToggleOffIcon fontSize="small" />}
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            title={t('admin.bannedWords.deleteButton')}
                            onClick={() => handleDelete(w)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <ErrorSnackbar
        open={snack.open}
        message={snack.message}
        severity={snack.severity}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      />
    </Box>
  );
}

// ─── Invariable Words Tab ─────────────────────────────────────────────────────

function InvariableWordsTab({ t }) {
  const [words, setWords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newWord, setNewWord] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });
  const [filterText, setFilterText] = useState('');
  const [sortField, setSortField] = useState('word');
  const [sortDir, setSortDir] = useState('asc');

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const displayedWords = useMemo(() => {
    let result = words;
    if (filterText.trim()) {
      const lower = filterText.trim().toLowerCase();
      result = result.filter((w) => w.word.toLowerCase().includes(lower));
    }
    return [...result].sort((a, b) => {
      let aVal = a[sortField] ?? '';
      let bVal = b[sortField] ?? '';
      if (sortField === 'createdAt') {
        aVal = aVal ? new Date(aVal).getTime() : 0;
        bVal = bVal ? new Date(bVal).getTime() : 0;
      } else {
        aVal = String(aVal).toLowerCase();
        bVal = String(bVal).toLowerCase();
      }
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [words, filterText, sortField, sortDir]);

  const loadWords = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiService.get('/api/v1/invariable-words');
      setWords(data);
    } catch {
      setWords([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadWords(); }, [loadWords]);

  const handleAdd = async () => {
    if (!newWord.trim()) return;
    setSubmitting(true);
    try {
      await apiService.post('/api/v1/invariable-words', { word: newWord.trim() });
      setNewWord('');
      setSnack({ open: true, message: t('admin.invariableWords.addSuccess'), severity: 'success' });
      loadWords();
    } catch (err) {
      setSnack({ open: true, message: err.message || t('common.error'), severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggle = async (word) => {
    try {
      await apiService.put(`/api/v1/invariable-words/${word.id}/toggle`, { active: !word.active });
      loadWords();
    } catch (err) {
      setSnack({ open: true, message: err.message || t('common.error'), severity: 'error' });
    }
  };

  const handleDelete = async (word) => {
    try {
      await apiService.delete(`/api/v1/invariable-words/${word.id}`);
      setSnack({ open: true, message: t('admin.invariableWords.deleteSuccess', { word: word.word }), severity: 'success' });
      loadWords();
    } catch (err) {
      setSnack({ open: true, message: err.message || t('common.error'), severity: 'error' });
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Card>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            {t('admin.invariableWords.addTitle')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <TextField
              label={t('admin.invariableWords.wordLabel')}
              value={newWord}
              onChange={(e) => setNewWord(e.target.value)}
              size="small"
              sx={{ flex: 1, minWidth: 150 }}
              inputProps={{ maxLength: 50 }}
              onKeyDown={(e) => { if (e.key === 'Enter') handleAdd(); }}
            />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAdd}
              disabled={submitting || !newWord.trim()}
            >
              {t('admin.invariableWords.addButton')}
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <TextField
            size="small"
            fullWidth
            placeholder={t('admin.invariableWords.filterPlaceholder')}
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />
          {loading ? (
            <LoadingSpinner />
          ) : (
            <TableContainer>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <TableSortLabel
                        active={sortField === 'word'}
                        direction={sortField === 'word' ? sortDir : 'asc'}
                        onClick={() => handleSort('word')}
                      >
                        {t('admin.invariableWords.colWord')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortField === 'active'}
                        direction={sortField === 'active' ? sortDir : 'asc'}
                        onClick={() => handleSort('active')}
                      >
                        {t('admin.invariableWords.colStatus')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortField === 'createdAt'}
                        direction={sortField === 'createdAt' ? sortDir : 'asc'}
                        onClick={() => handleSort('createdAt')}
                      >
                        {t('admin.invariableWords.colCreated')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>{t('admin.users.colActions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayedWords.map((w) => (
                    <TableRow key={w.id}>
                      <TableCell>{w.word}</TableCell>
                      <TableCell>
                        <Chip
                          label={w.active ? t('admin.active') : t('admin.inactive')}
                          color={w.active ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {w.createdAt ? new Date(w.createdAt).toLocaleDateString() : '—'}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <IconButton
                            size="small"
                            color={w.active ? 'default' : 'success'}
                            title={w.active ? t('admin.invariableWords.deactivate') : t('admin.invariableWords.activate')}
                            onClick={() => handleToggle(w)}
                          >
                            {w.active ? <ToggleOnIcon fontSize="small" /> : <ToggleOffIcon fontSize="small" />}
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            title={t('admin.invariableWords.deleteButton')}
                            onClick={() => handleDelete(w)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <ErrorSnackbar
        open={snack.open}
        message={snack.message}
        severity={snack.severity}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      />
    </Box>
  );
}

// ─── Config Tab ───────────────────────────────────────────────────────────────

function parseCron(cron) {
  // Formato esperado: "0 MM HH * * *"
  const parts = (cron || '').split(' ');
  const hour = parts[2] !== undefined ? parseInt(parts[2], 10) : 22;
  const minute = parts[1] !== undefined ? parseInt(parts[1], 10) : 5;
  return {
    hour: isNaN(hour) ? 22 : hour,
    minute: isNaN(minute) ? 5 : minute,
  };
}

function buildCron(hour, minute) {
  return `0 ${minute} ${hour} * * *`;
}

function ConfigTab({ t }) {
  const [hour, setHour] = useState(22);
  const [minute, setMinute] = useState(5);
  const [revealDelayMinutes, setRevealDelayMinutes] = useState(30);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rebuildingRankings, setRebuildingRankings] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });

  const [minVersionAndroid, setMinVersionAndroid] = useState('');
  const [minVersionIos, setMinVersionIos] = useState('');
  const [forceUpdateAndroid, setForceUpdateAndroid] = useState(false);
  const [forceUpdateIos, setForceUpdateIos] = useState(false);
  const [savingVersion, setSavingVersion] = useState(false);

  useEffect(() => {
    apiService.get('/api/v1/admin/config')
      .then((entries) => {
        const cronEntry = entries.find((e) => e.key === 'SCHEDULER_CRON');
        if (cronEntry) {
          const { hour: h, minute: m } = parseCron(cronEntry.value);
          setHour(h);
          setMinute(m);
        }
        const revealDelayEntry = entries.find((e) => e.key === 'REVEAL_DELAY_MINUTES');
        if (revealDelayEntry) {
          setRevealDelayMinutes(parseInt(revealDelayEntry.value, 10) || 30);
        }
        const minVersionAndroidEntry = entries.find((e) => e.key === 'MIN_VERSION_ANDROID');
        if (minVersionAndroidEntry) setMinVersionAndroid(minVersionAndroidEntry.value);
        const minVersionIosEntry = entries.find((e) => e.key === 'MIN_VERSION_IOS');
        if (minVersionIosEntry) setMinVersionIos(minVersionIosEntry.value);
        const forceUpdateAndroidEntry = entries.find((e) => e.key === 'FORCE_UPDATE_ANDROID');
        if (forceUpdateAndroidEntry) setForceUpdateAndroid(forceUpdateAndroidEntry.value === 'true');
        const forceUpdateIosEntry = entries.find((e) => e.key === 'FORCE_UPDATE_IOS');
        if (forceUpdateIosEntry) setForceUpdateIos(forceUpdateIosEntry.value === 'true');
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await Promise.all([
        apiService.put('/api/v1/admin/config/SCHEDULER_CRON', { value: buildCron(hour, minute) }),
        apiService.put('/api/v1/admin/config/REVEAL_DELAY_MINUTES', { value: String(revealDelayMinutes) }),
      ]);
      setSnack({ open: true, message: t('admin.config.saveSuccess'), severity: 'success' });
    } catch (err) {
      setSnack({ open: true, message: err.message || t('common.error'), severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveVersion = async () => {
    setSavingVersion(true);
    try {
      await Promise.all([
        apiService.put('/api/v1/admin/config/MIN_VERSION_ANDROID', { value: minVersionAndroid.trim() }),
        apiService.put('/api/v1/admin/config/MIN_VERSION_IOS', { value: minVersionIos.trim() }),
        apiService.put('/api/v1/admin/config/FORCE_UPDATE_ANDROID', { value: String(forceUpdateAndroid) }),
        apiService.put('/api/v1/admin/config/FORCE_UPDATE_IOS', { value: String(forceUpdateIos) }),
      ]);
      setSnack({ open: true, message: t('admin.config.versionSaveSuccess'), severity: 'success' });
    } catch (err) {
      setSnack({ open: true, message: err.message || t('common.error'), severity: 'error' });
    } finally {
      setSavingVersion(false);
    }
  };

  const handleRebuildRankings = async () => {
    setRebuildingRankings(true);
    try {
      await apiService.post('/api/v1/admin/trigger');
      setSnack({ open: true, message: t('admin.config.rebuildRankingsSuccess'), severity: 'success' });
    } catch (err) {
      setSnack({ open: true, message: err.message || t('common.error'), severity: 'error' });
    } finally {
      setRebuildingRankings(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Card>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            {t('admin.config.scheduleTitle')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('admin.config.currentSchedule')}: {String(hour).padStart(2, '0')}:{String(minute).padStart(2, '0')} ({t('admin.config.timezone')})
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <TextField
              label={t('admin.config.hourLabel')}
              type="number"
              value={hour}
              onChange={(e) => setHour(Math.max(0, Math.min(23, parseInt(e.target.value, 10) || 0)))}
              size="small"
              sx={{ width: 100 }}
              inputProps={{ min: 0, max: 23 }}
            />
            <TextField
              label={t('admin.config.minuteLabel')}
              type="number"
              value={minute}
              onChange={(e) => setMinute(Math.max(0, Math.min(59, parseInt(e.target.value, 10) || 0)))}
              size="small"
              sx={{ width: 100 }}
              inputProps={{ min: 0, max: 59 }}
            />
            <TextField
              label={t('admin.config.revealDelayLabel')}
              type="number"
              value={revealDelayMinutes}
              onChange={(e) => setRevealDelayMinutes(Math.max(0, Math.min(180, parseInt(e.target.value, 10) || 0)))}
              size="small"
              sx={{ width: 160 }}
              inputProps={{ min: 0, max: 180 }}
              helperText={t('admin.config.revealDelayHint')}
            />
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving}
            >
              {t('common.save')}
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            {t('admin.config.rebuildRankingsTitle')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('admin.config.rebuildRankingsDesc')}
          </Typography>
          <Button
            variant="outlined"
            color="warning"
            startIcon={<PlayArrowIcon />}
            onClick={handleRebuildRankings}
            disabled={rebuildingRankings}
          >
            {rebuildingRankings ? t('admin.config.rebuildRankingsLoading') : t('admin.config.rebuildRankingsButton')}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            {t('admin.config.versionTitle')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('admin.config.versionDesc')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
            <TextField
              label={t('admin.config.minVersionAndroidLabel')}
              value={minVersionAndroid}
              onChange={(e) => setMinVersionAndroid(e.target.value)}
              size="small"
              sx={{ width: 200 }}
              placeholder="0.9.0"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={forceUpdateAndroid}
                  onChange={(e) => setForceUpdateAndroid(e.target.checked)}
                />
              }
              label={t('admin.config.forceUpdateAndroidLabel')}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
            <TextField
              label={t('admin.config.minVersionIosLabel')}
              value={minVersionIos}
              onChange={(e) => setMinVersionIos(e.target.value)}
              size="small"
              sx={{ width: 200 }}
              placeholder="0.9.0"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={forceUpdateIos}
                  onChange={(e) => setForceUpdateIos(e.target.checked)}
                />
              }
              label={t('admin.config.forceUpdateIosLabel')}
            />
          </Box>
          <Button
            variant="contained"
            onClick={handleSaveVersion}
            disabled={savingVersion}
          >
            {t('common.save')}
          </Button>
        </CardContent>
      </Card>

      <ErrorSnackbar
        open={snack.open}
        message={snack.message}
        severity={snack.severity}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      />
    </Box>
  );
}

// ─── Suggestions Tab ─────────────────────────────────────────────────────────

function SuggestionsTab({ t, isMobile }) {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scheduleTarget, setScheduleTarget] = useState(null); // suggestion to schedule
  const [scheduleDate, setScheduleDate] = useState(tomorrow());
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });

  const loadSuggestions = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiService.get('/api/v1/suggestions/admin');
      setSuggestions(Array.isArray(data) ? data : []);
    } catch {
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadSuggestions(); }, [loadSuggestions]);

  const handleSchedule = async () => {
    if (!scheduleTarget) return;
    try {
      await apiService.post(`/api/v1/suggestions/admin/${scheduleTarget.id}/schedule`, { date: scheduleDate });
      setSnack({ open: true, message: t('admin.suggestions.scheduleSuccess', { topic: scheduleTarget.topic }), severity: 'success' });
      setScheduleTarget(null);
      loadSuggestions();
    } catch (err) {
      setSnack({ open: true, message: err.message || t('common.error'), severity: 'error' });
    }
  };

  const handleReject = async (suggestion) => {
    try {
      await apiService.delete(`/api/v1/suggestions/admin/${suggestion.id}`);
      setSnack({ open: true, message: t('admin.suggestions.rejectSuccess', { topic: suggestion.topic }), severity: 'success' });
      loadSuggestions();
    } catch (err) {
      setSnack({ open: true, message: err.message || t('common.error'), severity: 'error' });
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Card>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            {t('admin.suggestions.listTitle')}
          </Typography>
          {loading ? (
            <LoadingSpinner />
          ) : suggestions.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t('admin.suggestions.empty')}
            </Typography>
          ) : isMobile ? (
            <Stack divider={<Divider />}>
              {suggestions.map((s) => (
                <Box key={s.id} sx={{ py: 1.5 }}>
                  <Typography variant="body2" fontWeight={500} sx={{ mb: 0.5 }}>{s.topic}</Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                      {s.userName || '—'} · {s.createdAt ? new Date(s.createdAt).toLocaleDateString() : '—'}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, ml: 1, flexShrink: 0 }}>
                      <IconButton
                        size="small"
                        color="success"
                        title={t('admin.suggestions.scheduleButton')}
                        onClick={() => { setScheduleTarget(s); setScheduleDate(tomorrow()); }}
                      >
                        <CheckCircleOutlineIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        title={t('admin.suggestions.rejectButton')}
                        onClick={() => handleReject(s)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                </Box>
              ))}
            </Stack>
          ) : (
            <TableContainer>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('admin.topics.colTopic')}</TableCell>
                    <TableCell>{t('admin.suggestions.colUser')}</TableCell>
                    <TableCell>{t('admin.suggestions.colDate')}</TableCell>
                    <TableCell>{t('admin.users.colActions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {suggestions.map((s) => (
                    <TableRow key={s.id}>
                      <TableCell>{s.topic}</TableCell>
                      <TableCell>{s.userName || '—'}</TableCell>
                      <TableCell>{s.createdAt ? new Date(s.createdAt).toLocaleDateString() : '—'}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <IconButton
                            size="small"
                            color="success"
                            title={t('admin.suggestions.scheduleButton')}
                            onClick={() => { setScheduleTarget(s); setScheduleDate(tomorrow()); }}
                          >
                            <CheckCircleOutlineIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            title={t('admin.suggestions.rejectButton')}
                            onClick={() => handleReject(s)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!scheduleTarget} onClose={() => setScheduleTarget(null)}>
        <DialogTitle>{t('admin.suggestions.scheduleTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t('admin.suggestions.scheduleText', { topic: scheduleTarget?.topic || '' })}
          </DialogContentText>
          <TextField
            label={t('admin.topics.dateLabel')}
            type="date"
            value={scheduleDate}
            onChange={(e) => setScheduleDate(e.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScheduleTarget(null)}>{t('common.cancel')}</Button>
          <Button onClick={handleSchedule} color="success" variant="contained">
            {t('admin.suggestions.scheduleConfirm')}
          </Button>
        </DialogActions>
      </Dialog>

      <ErrorSnackbar
        open={snack.open}
        message={snack.message}
        severity={snack.severity}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      />
    </Box>
  );
}

// ─── Notifications Tab ────────────────────────────────────────────────────────

const NOTIF_TYPES = ['NEW_TOPIC', 'RESULTS', 'REMINDER'];
const NOTIF_LANGUAGES = ['es', 'en', 'pt', 'fr', 'de'];

const EMPTY_MSG = { type: 'NEW_TOPIC', language: 'es', titleTemplate: '', bodyTemplate: '', active: true };

function NotificationsTab({ t }) {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [activeTokens, setActiveTokens] = useState(null);
  const [newTopicHour, setNewTopicHour] = useState(9);
  const [reminderHoursBefore, setReminderHoursBefore] = useState(1);
  const [savingConfig, setSavingConfig] = useState(false);

  // Gestión de mensajes
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(true);
  const [filterType, setFilterType] = useState('NEW_TOPIC');
  const [filterLang, setFilterLang] = useState('');
  const [editingMsg, setEditingMsg] = useState(null);   // mensaje en edición o null
  const [editForm, setEditForm] = useState(EMPTY_MSG);  // formulario de edición/creación
  const [isCreating, setIsCreating] = useState(false);

  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });

  const loadMessages = useCallback(async () => {
    setLoadingMessages(true);
    try {
      const data = await apiService.get(`/api/v1/notifications/messages?type=${filterType}`);
      setMessages(Array.isArray(data) ? data : []);
    } catch { setMessages([]); }
    finally { setLoadingMessages(false); }
  }, [filterType]);

  useEffect(() => {
    apiService.get('/api/v1/notifications/stats').then((d) => setActiveTokens(d.activeTokens)).catch(() => {});
    apiService.get('/api/v1/admin/config').then((entries) => {
      const h = entries.find((e) => e.key === 'NOTIF_NEW_TOPIC_HOUR');
      const r = entries.find((e) => e.key === 'NOTIF_REMINDER_HOURS_BEFORE');
      if (h) setNewTopicHour(parseInt(h.value, 10) || 9);
      if (r) setReminderHoursBefore(parseInt(r.value, 10) || 1);
    }).catch(() => {});
  }, []);

  useEffect(() => { loadMessages(); }, [loadMessages]);

  const handleSend = async () => {
    if (!title.trim() || !body.trim()) return;
    setSending(true);
    try {
      await apiService.post('/api/v1/notifications/send', { title: title.trim(), body: body.trim() });
      setSnack({ open: true, message: t('admin.notifications.sendSuccess'), severity: 'success' });
      setTitle(''); setBody('');
    } catch (err) {
      setSnack({ open: true, message: err.message || t('common.error'), severity: 'error' });
    } finally { setSending(false); }
  };

  const handleSaveConfig = async () => {
    setSavingConfig(true);
    try {
      await Promise.all([
        apiService.put('/api/v1/admin/config/NOTIF_NEW_TOPIC_HOUR', { value: String(newTopicHour) }),
        apiService.put('/api/v1/admin/config/NOTIF_REMINDER_HOURS_BEFORE', { value: String(reminderHoursBefore) }),
      ]);
      setSnack({ open: true, message: t('admin.config.saveSuccess'), severity: 'success' });
    } catch (err) {
      setSnack({ open: true, message: err.message || t('common.error'), severity: 'error' });
    } finally { setSavingConfig(false); }
  };

  const handleToggle = async (msg) => {
    try {
      const updated = await apiService.request('PATCH', `/api/v1/notifications/messages/${msg.id}/toggle`);
      setMessages((prev) => prev.map((m) => m.id === msg.id ? updated : m));
    } catch (err) {
      setSnack({ open: true, message: err.message || t('common.error'), severity: 'error' });
    }
  };

  const handleDelete = async (msg) => {
    try {
      await apiService.delete(`/api/v1/notifications/messages/${msg.id}`);
      setMessages((prev) => prev.filter((m) => m.id !== msg.id));
      setSnack({ open: true, message: t('admin.notifications.msgDeleteSuccess'), severity: 'success' });
    } catch (err) {
      setSnack({ open: true, message: err.message || t('common.error'), severity: 'error' });
    }
  };

  const handleSaveMsg = async () => {
    try {
      if (isCreating) {
        const created = await apiService.post('/api/v1/notifications/messages', editForm);
        if (created.type === filterType) setMessages((prev) => [...prev, created]);
        setSnack({ open: true, message: t('admin.notifications.msgCreateSuccess'), severity: 'success' });
      } else {
        const updated = await apiService.put(`/api/v1/notifications/messages/${editForm.id}`, editForm);
        setMessages((prev) => prev.map((m) => m.id === editForm.id ? updated : m));
        setSnack({ open: true, message: t('admin.notifications.msgSaveSuccess'), severity: 'success' });
      }
      setEditingMsg(null); setIsCreating(false);
    } catch (err) {
      setSnack({ open: true, message: err.message || t('common.error'), severity: 'error' });
    }
  };

  const openEdit = (msg) => { setEditingMsg(msg); setEditForm({ ...msg }); setIsCreating(false); };
  const openCreate = () => { setEditingMsg({}); setEditForm({ ...EMPTY_MSG, type: filterType }); setIsCreating(true); };

  const filteredMessages = filterLang
    ? messages.filter((m) => m.language === filterLang)
    : messages;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

      {/* Estadísticas */}
      <Card>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            {t('admin.notifications.statsTitle')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('admin.notifications.activeTokens')}{': '}
            <strong>{activeTokens !== null ? activeTokens : '—'}</strong>
          </Typography>
        </CardContent>
      </Card>

      {/* Envío global manual */}
      <Card>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            {t('admin.notifications.sendTitle')}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField label={t('admin.notifications.titleLabel')} value={title} onChange={(e) => setTitle(e.target.value)} size="small" inputProps={{ maxLength: 100 }} fullWidth />
            <TextField label={t('admin.notifications.bodyLabel')} value={body} onChange={(e) => setBody(e.target.value)} size="small" multiline minRows={2} inputProps={{ maxLength: 300 }} fullWidth />
            <Box>
              <Button variant="contained" onClick={handleSend} disabled={sending || !title.trim() || !body.trim()}>
                {t('admin.notifications.sendButton')}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Configuración de horarios */}
      <Card>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>{t('admin.notifications.scheduleTitle')}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{t('admin.config.timezone')}</Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <TextField label={t('admin.notifications.newTopicHourLabel')} type="number" value={newTopicHour} onChange={(e) => setNewTopicHour(Math.max(0, Math.min(23, parseInt(e.target.value, 10) || 0)))} size="small" sx={{ width: 140 }} inputProps={{ min: 0, max: 23 }} helperText={t('admin.notifications.newTopicHourHint')} />
            <TextField label={t('admin.notifications.reminderHoursLabel')} type="number" value={reminderHoursBefore} onChange={(e) => setReminderHoursBefore(Math.max(1, Math.min(12, parseInt(e.target.value, 10) || 1)))} size="small" sx={{ width: 160 }} inputProps={{ min: 1, max: 12 }} helperText={t('admin.notifications.reminderHoursHint')} />
            <Button variant="contained" onClick={handleSaveConfig} disabled={savingConfig}>{t('common.save')}</Button>
          </Box>
        </CardContent>
      </Card>

      {/* Gestión de mensajes */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="subtitle1" fontWeight={600}>{t('admin.notifications.messagesTitle')}</Typography>
            <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openCreate}>
              {t('admin.notifications.msgAddButton')}
            </Button>
          </Box>

          {/* Filtros */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>{t('admin.notifications.msgTypeLabel')}</InputLabel>
              <Select value={filterType} label={t('admin.notifications.msgTypeLabel')} onChange={(e) => setFilterType(e.target.value)}>
                {NOTIF_TYPES.map((tp) => <MenuItem key={tp} value={tp}>{t(`admin.notifications.type_${tp}`)}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>{t('admin.notifications.msgLangFilter')}</InputLabel>
              <Select value={filterLang} label={t('admin.notifications.msgLangFilter')} onChange={(e) => setFilterLang(e.target.value)}>
                <MenuItem value="">{t('admin.notifications.msgLangAll')}</MenuItem>
                {NOTIF_LANGUAGES.map((l) => <MenuItem key={l} value={l}>{l.toUpperCase()}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>

          {loadingMessages ? <LoadingSpinner /> : filteredMessages.length === 0 ? (
            <Typography variant="body2" color="text.secondary">{t('admin.notifications.msgEmpty')}</Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 50 }}>{t('admin.notifications.msgColLang')}</TableCell>
                    <TableCell>{t('admin.notifications.msgColTitle')}</TableCell>
                    <TableCell>{t('admin.notifications.msgColBody')}</TableCell>
                    <TableCell sx={{ width: 90 }}>{t('admin.notifications.msgColStatus')}</TableCell>
                    <TableCell sx={{ width: 100 }}>{t('admin.users.colActions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredMessages.map((msg) => (
                    <TableRow key={msg.id} sx={{ opacity: msg.active ? 1 : 0.45 }}>
                      <TableCell><Chip label={msg.language.toUpperCase()} size="small" /></TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {msg.titleTemplate}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {msg.bodyTemplate}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={msg.active ? t('admin.active') : t('admin.inactive')}
                          color={msg.active ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <IconButton size="small" title={t('common.save')} onClick={() => openEdit(msg)}>
                            <CheckCircleOutlineIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" color={msg.active ? 'default' : 'success'} title={msg.active ? t('admin.bannedWords.deactivate') : t('admin.bannedWords.activate')} onClick={() => handleToggle(msg)}>
                            {msg.active ? <ToggleOnIcon fontSize="small" /> : <ToggleOffIcon fontSize="small" />}
                          </IconButton>
                          <IconButton size="small" color="error" title={t('admin.bannedWords.deleteButton')} onClick={() => handleDelete(msg)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Diálogo edición / creación */}
      <Dialog open={!!editingMsg} onClose={() => { setEditingMsg(null); setIsCreating(false); }} maxWidth="sm" fullWidth>
        <DialogTitle>{isCreating ? t('admin.notifications.msgCreateTitle') : t('admin.notifications.msgEditTitle')}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
          {isCreating && (
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>{t('admin.notifications.msgTypeLabel')}</InputLabel>
                <Select value={editForm.type} label={t('admin.notifications.msgTypeLabel')} onChange={(e) => setEditForm((f) => ({ ...f, type: e.target.value }))}>
                  {NOTIF_TYPES.map((tp) => <MenuItem key={tp} value={tp}>{t(`admin.notifications.type_${tp}`)}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl size="small" fullWidth>
                <InputLabel>{t('admin.notifications.msgColLang')}</InputLabel>
                <Select value={editForm.language} label={t('admin.notifications.msgColLang')} onChange={(e) => setEditForm((f) => ({ ...f, language: e.target.value }))}>
                  {NOTIF_LANGUAGES.map((l) => <MenuItem key={l} value={l}>{l.toUpperCase()}</MenuItem>)}
                </Select>
              </FormControl>
            </Box>
          )}
          <TextField
            label={t('admin.notifications.msgColTitle')}
            value={editForm.titleTemplate || ''}
            onChange={(e) => setEditForm((f) => ({ ...f, titleTemplate: e.target.value }))}
            size="small"
            fullWidth
            inputProps={{ maxLength: 200 }}
            helperText={t('admin.notifications.msgVarHint')}
          />
          <TextField
            label={t('admin.notifications.msgColBody')}
            value={editForm.bodyTemplate || ''}
            onChange={(e) => setEditForm((f) => ({ ...f, bodyTemplate: e.target.value }))}
            size="small"
            fullWidth
            multiline
            minRows={3}
            inputProps={{ maxLength: 500 }}
            helperText={t('admin.notifications.msgVarHint')}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setEditingMsg(null); setIsCreating(false); }}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={handleSaveMsg} disabled={!editForm.titleTemplate?.trim() || !editForm.bodyTemplate?.trim()}>
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      <ErrorSnackbar open={snack.open} message={snack.message} severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, open: false }))} />
    </Box>
  );
}

// ─── Main AdminPage ───────────────────────────────────────────────────────────

const TAB_LABELS = (t) => [
  t('admin.tabs.topics'),
  t('admin.tabs.suggestions'),
  t('admin.tabs.users'),
  t('admin.tabs.bannedWords'),
  t('admin.tabs.invariableWords'),
  t('admin.tabs.config'),
  t('admin.tabs.notifications'),
];

export default function AdminPage() {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [tab, setTab] = useState(0);

  const labels = TAB_LABELS(t);

  return (
    <Box className="page-content" sx={{ gap: 2, maxWidth: 900, mx: 'auto', width: '100%' }}>
      <Typography
        variant="h5"
        fontWeight={700}
        textAlign="center"
        color="white"
        sx={{ textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}
      >
        {t('admin.title')}
      </Typography>

      <Card sx={{ width: '100%', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        {isMobile ? (
          <Box sx={{ px: 2, pt: 2, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
            <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
              <InputLabel>{t('admin.tabs.label')}</InputLabel>
              <Select
                value={tab}
                label={t('admin.tabs.label')}
                onChange={(e) => setTab(e.target.value)}
              >
                {labels.map((label, i) => (
                  <MenuItem key={i} value={i}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        ) : (
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{ borderBottom: 1, borderColor: 'divider', px: 2, flexShrink: 0 }}
          >
            {labels.map((label, i) => (
              <Tab key={i} label={label} />
            ))}
          </Tabs>
        )}
        <CardContent sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
          {tab === 0 && <TopicsTab t={t} isMobile={isMobile} />}
          {tab === 1 && <SuggestionsTab t={t} isMobile={isMobile} />}
          {tab === 2 && <UsersTab t={t} isMobile={isMobile} />}
          {tab === 3 && <BannedWordsTab t={t} />}
          {tab === 4 && <InvariableWordsTab t={t} />}
          {tab === 5 && <ConfigTab t={t} />}
          {tab === 6 && <NotificationsTab t={t} />}
        </CardContent>
      </Card>
    </Box>
  );
}
